import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../ui/form";
import { MetadataService } from "../../services/metadataService";

const CreateLikelihoodForm = ({
  open,
  onClose,
  organizationId,
  onLikelihoodCreated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    value: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.value || !formData.description.trim()) {
      setError("Both value and description are required");
      return;
    }

    const valueNum = parseInt(formData.value);
    if (isNaN(valueNum) || valueNum < 1) {
      setError("Value must be a positive number");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const likelihoodData = {
        value: valueNum,
        description: formData.description.trim(),
        organization_id: organizationId,
      };

      await MetadataService.createLikelihood(
        likelihoodData,
        getAccessTokenSilently
      );

      // Reset form
      setFormData({
        value: "",
        description: "",
      });

      onLikelihoodCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        value: "",
        description: "",
      });
      setError(null);
      onClose();
    }
  };

  const getLikelihoodLevel = () => {
    const value = parseInt(formData.value);
    if (isNaN(value)) return null;

    if (value >= 8) return { level: "Almost Certain", variant: "destructive" };
    if (value >= 6) return { level: "Likely", variant: "secondary" };
    if (value >= 4) return { level: "Possible", variant: "outline" };
    if (value >= 2) return { level: "Unlikely", variant: "secondary" };
    return { level: "Rare", variant: "secondary" };
  };

  const likelihoodLevel = getLikelihoodLevel();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Likelihood</DialogTitle>
        </DialogHeader>

        <Form className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}

          <FormItem>
            <FormLabel>Likelihood Value *</FormLabel>
            <FormControl>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => handleInputChange("value", e.target.value)}
                placeholder="Enter numeric value"
                disabled={submitting}
                min={1}
              />
            </FormControl>
          </FormItem>

          <FormItem>
            <FormLabel>Description *</FormLabel>
            <FormControl>
              <Input
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="e.g., Rare, Unlikely, Possible, Likely, Almost Certain"
                disabled={submitting}
              />
            </FormControl>
          </FormItem>
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Creating...
              </>
            ) : (
              "Create Likelihood"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLikelihoodForm;
